import { Chip, Stack, Tooltip } from '@mui/material';
import {
  formatBalancePolicy,
  formatSolveStatus,
  type AppLocale,
  type LocaleBundle,
} from '../../../i18n';
import type { PresentationModel } from '../../../presentation';
import type { BalancePolicy, SolveStatus } from '../../../solver';
import type { SnapshotMetricId } from './solveSnapshotMetadata';
import { getSnapshotMetricDescription } from './solveSnapshotMetadata';
import { snapshotFormalTooltipSlotProps } from '../workbenchStyles';

export interface SolveSnapshotSummaryChipsProps {
  bundle: LocaleBundle;
  locale: AppLocale;
  requestSummary?: PresentationModel['requestSummary'];
  balancePolicy: BalancePolicy;
  status: SolveStatus | null;
  activityLabel?: string;
}

function renderMetricChip(
  metricId: SnapshotMetricId,
  label: string,
  descriptions: Record<SnapshotMetricId, string>,
  color?: 'default' | 'success' | 'warning'
) {
  return (
    <Tooltip
      key={metricId}
      title={descriptions[metricId]}
      slotProps={snapshotFormalTooltipSlotProps}
    >
      <Chip size="small" variant="outlined" color={color} label={label} />
    </Tooltip>
  );
}

export default function SolveSnapshotSummaryChips({
  bundle,
  locale,
  balancePolicy,
  status,
  activityLabel,
}: SolveSnapshotSummaryChipsProps) {
  const metricDescriptions = getSnapshotMetricDescription(bundle);
  return (
    <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.75}>
      {renderMetricChip(
        'balance',
        `${bundle.summary.balanceLabel}: ${formatBalancePolicy(balancePolicy, locale)}`,
        metricDescriptions
      )}
      {renderMetricChip(
        'status',
        `${bundle.summary.statusLabel}: ${formatSolveStatus(status, locale)}`,
        metricDescriptions,
        status === 'optimal' ? 'success' : status === 'feasible' ? 'warning' : 'default'
      )}
      {activityLabel
        ? renderMetricChip(
            'activity',
            `${bundle.solveActivity.metricLabel}: ${activityLabel}`,
            metricDescriptions,
            'warning'
          )
        : null}
    </Stack>
  );
}
